// Marketing landing page under /landing. Has a header with a "Demo starten"
// shortcut that takes the user back to the main app with ?demo=1, a hero,
// feature grid, defibrillator map section, and a footer.

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    PlayCircle,
    Languages,
    Heart,
    Zap,
    QrCode,
    Volume2,
    Smartphone,
    ArrowRight,
    ShieldCheck,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { FirstAidSymbol } from "@/components/FirstAidSymbol";
import { LanguageGlobe } from "@/components/LanguageGlobe";
import { StickerToast } from "@/components/StickerToast";

const FEATURES = [
    {
        icon: Heart,
        titleKey: "feature_triage_title",
        bodyKey: "feature_triage_body",
        accent: "text-[#A72920] bg-[#A72920]/10",
    },
    {
        icon: Volume2,
        titleKey: "feature_voice_title",
        bodyKey: "feature_voice_body",
        accent: "text-[#245288] bg-[#245288]/10",
    },
    {
        icon: Languages,
        titleKey: "feature_lang_title",
        bodyKey: "feature_lang_body",
        accent: "text-emerald-700 bg-emerald-100",
    },
    {
        icon: Zap,
        titleKey: "feature_cpr_title",
        bodyKey: "feature_cpr_body",
        accent: "text-amber-700 bg-amber-100",
    },
    {
        icon: QrCode,
        titleKey: "feature_qr_title",
        bodyKey: "feature_qr_body",
        accent: "text-indigo-700 bg-indigo-100",
    },
    {
        icon: Smartphone,
        titleKey: "feature_mobile_title",
        bodyKey: "feature_mobile_body",
        accent: "text-zinc-800 bg-zinc-100",
    },
];

export default function LandingPage() {
    const { t } = useI18n();

    useEffect(() => {
        document.title = "SafetyAssist – Erste Hilfe, sofort. | Makrolog";
    }, []);

    return (
        <div className="min-h-screen bg-zinc-50">
            <MarketingHeader />

            {/* HERO */}
            <section className="bg-gradient-to-br from-[#245288] via-[#1c4374] to-[#0f8040] text-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-white/80 bg-white/15 rounded-full px-3 py-1">
                            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
                            {t("hero_eyebrow")}
                        </div>
                        <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
                            {t("hero_title")}
                        </h1>
                        <p className="mt-5 text-base sm:text-lg text-white/85 max-w-xl leading-relaxed">
                            {t("hero_subtitle")}
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <Link
                                to="/?demo=1"
                                data-testid="hero-demo-button"
                                className="inline-flex items-center gap-2 bg-white text-[#245288] font-display font-black px-5 py-3 rounded-2xl hover:bg-zinc-100 transition shadow-lg"
                            >
                                <PlayCircle className="w-5 h-5" strokeWidth={2.5} />
                                {t("hero_cta_demo")}
                            </Link>
                            <Link
                                to="/"
                                data-testid="hero-app-button"
                                className="inline-flex items-center gap-2 bg-[#A72920] text-white font-display font-black px-5 py-3 rounded-2xl hover:bg-[#8e2019] transition shadow-lg"
                            >
                                <Heart className="w-5 h-5" strokeWidth={2.5} />
                                {t("hero_cta_app")}
                            </Link>
                        </div>
                    </div>

                    <div className="hidden lg:flex justify-center">
                        <div className="relative">
                            {/* Soft glow behind the poster */}
                            <div
                                aria-hidden
                                className="absolute -inset-6 bg-white/10 rounded-3xl blur-2xl"
                            />
                            <img
                                data-testid="hero-poster"
                                src="https://customer-assets.emergentagent.com/job_rapid-response-213/artifacts/6653rwlt_204-001-overlay%20%282%29.jpg"
                                alt={t("hero_poster_alt")}
                                className="relative w-[420px] xl:w-[480px] rounded-2xl shadow-2xl ring-1 ring-white/20 rotate-[2deg] hover:rotate-0 transition-transform duration-500"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
                <div className="max-w-2xl">
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-zinc-900 leading-tight">
                        {t("features_title")}
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-zinc-600 leading-relaxed">
                        {t("features_subtitle")}
                    </p>
                </div>

                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                    {FEATURES.map((f, i) => {
                        const Icon = f.icon;
                        return (
                            <div
                                key={f.titleKey}
                                data-testid={`feature-${i}`}
                                className="bg-white border border-zinc-200 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md transition"
                            >
                                <div
                                    className={`w-12 h-12 rounded-xl ${f.accent} flex items-center justify-center`}
                                >
                                    <Icon className="w-6 h-6" strokeWidth={2.2} />
                                </div>
                                <h3 className="mt-4 font-display font-black text-lg sm:text-xl tracking-tight text-zinc-900">
                                    {t(f.titleKey)}
                                </h3>
                                <p className="mt-2 text-sm sm:text-[15px] text-zinc-600 leading-relaxed">
                                    {t(f.bodyKey)}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </section>

            <MarketingFooter />
            <StickerToast />
        </div>
    );
}

const SAFETY_ASSIST_LOGO =
    "https://www.safety-assist.de/wp-content/uploads/2025/03/Safety-Assist-Logo-full-1.png";

const MarketingHeader = () => {
    const { t } = useI18n();
    return (
        <header
            data-testid="marketing-header"
            className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-zinc-200"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
                <Link
                    to="/"
                    data-testid="marketing-header-logo"
                    aria-label="SafetyAssist"
                    className="block opacity-95 hover:opacity-100 transition"
                >
                    <img
                        src={SAFETY_ASSIST_LOGO}
                        alt="SafetyAssist"
                        className="h-9 sm:h-10 w-auto max-w-[180px] sm:max-w-[220px] object-contain"
                        loading="eager"
                    />
                </Link>
                <div className="flex items-center gap-2 sm:gap-3">
                    <div className="hidden sm:block">
                        <LanguageGlobe variant="onLight" />
                    </div>
                    <Link
                        to="/?demo=1"
                        data-testid="header-demo-button"
                        className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white font-display font-bold text-sm sm:text-base px-3 sm:px-4 py-2 rounded-xl transition"
                    >
                        <PlayCircle className="w-4 h-4" strokeWidth={2.5} />
                        {t("header_demo")}
                    </Link>
                </div>
            </div>
        </header>
    );
};

const MarketingFooter = () => {
    const { t } = useI18n();
    return (
        <footer className="bg-zinc-900 text-zinc-300 mt-0">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid sm:grid-cols-2 gap-8">
                <div>
                    <Link
                        to="/"
                        data-testid="marketing-footer-logo"
                        aria-label="SafetyAssist"
                        className="inline-flex bg-white rounded-lg px-3 py-2"
                    >
                        <img
                            src={SAFETY_ASSIST_LOGO}
                            alt="SafetyAssist"
                            className="h-8 sm:h-9 w-auto max-w-[180px] object-contain"
                            loading="lazy"
                        />
                    </Link>
                    <p className="mt-3 text-sm text-zinc-400 max-w-md leading-relaxed">
                        {t("footer_tagline")}
                    </p>
                </div>
                <div className="flex sm:justify-end items-end">
                    <div className="text-sm space-y-2">
                        <Link
                            to="/"
                            className="block text-zinc-300 hover:text-white inline-flex items-center gap-1"
                        >
                            {t("footer_to_app")}
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <div className="text-zinc-500 text-xs">
                            {t("footer_powered_by")} · Makrolog AG
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};
